import React, { useEffect, useState } from 'react';
import {
  Button, Form, Card, Row, Col, Spinner, Table, Badge, Alert,
} from 'react-bootstrap';
import PricingService from '../../../Services/PricingService';

function PricingReport({ vehicleCategories }) {
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState(['EUR', 'HUF']);
  const [format, setFormat] = useState('excel');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [downloadInfo, setDownloadInfo] = useState(null); // For async Excel downloads

  const fetchAvailableRoutes = async () => {
    try {
      const response = await PricingService.fetchAvailableReportRoutes();
      setAvailableRoutes(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  useEffect(() => {
    fetchAvailableRoutes();
  }, []);

  const handleRouteToggle = (routeId) => {
    setSelectedRoutes((prev) => (prev.includes(routeId)
      ? prev.filter((id) => id !== routeId)
      : [...prev, routeId]));
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => (prev.includes(categoryId)
      ? prev.filter((id) => id !== categoryId)
      : [...prev, categoryId]));
  };

  const handleSelectAllRoutes = () => {
    if (selectedRoutes.length === availableRoutes.length) {
      setSelectedRoutes([]);
    } else {
      setSelectedRoutes(availableRoutes.map((r) => r.id));
    }
  };

  const handleSelectAllCategories = () => {
    if (selectedCategories.length === vehicleCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(vehicleCategories.map((c) => c.Id));
    }
  };

  const handleCurrencyToggle = (currency) => {
    setSelectedCurrencies((prev) => {
      if (prev.includes(currency)) {
        // Don't allow deselecting if it's the only one selected
        if (prev.length === 1) return prev;
        return prev.filter((c) => c !== currency);
      }
      return [...prev, currency];
    });
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    setPreviewData(null);
    try {
      const response = await PricingService.generatePricingReport({
        format: 'json',
        routeIds: selectedRoutes.length > 0 ? selectedRoutes : undefined,
        vehicleCategoryIds: selectedCategories.length > 0 ? selectedCategories : undefined,
        currencies: selectedCurrencies,
      });
      setPreviewData(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    setError('');
    setDownloadInfo(null);
    try {
      // Both Excel and CSV are generated asynchronously - we get a download URL
      const response = await PricingService.generatePricingReport({
        format,
        routeIds: selectedRoutes.length > 0 ? selectedRoutes : undefined,
        vehicleCategoryIds: selectedCategories.length > 0 ? selectedCategories : undefined,
        currencies: selectedCurrencies,
      });

      const data = response?.data?.data || response?.data;

      // Check if this is an async response with downloadUrl
      if (data?.downloadUrl && data?.status === 'processing') {
        setDownloadInfo({
          url: data.downloadUrl,
          filename: data.filename,
          message: data.message,
        });
      } else {
        setError('Unexpected response format from server');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Group routes by destination for better organization
  const groupedRoutes = availableRoutes.reduce((acc, route) => {
    const key = route.destination.name;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(route);
    return acc;
  }, {});

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Pricing Report Generator</h4>
      </div>

      <p className="text-muted mb-4">
        Generate a comprehensive pricing report for knowledge base integration.
        Select routes and vehicle categories, then download as Excel or CSV.
      </p>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        {/* Route Selection */}
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Routes</strong>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleSelectAllRoutes}
              >
                {selectedRoutes.length === availableRoutes.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {Object.entries(groupedRoutes).map(([destination, routes]) => (
                <div key={destination} className="mb-3">
                  <h6 className="text-muted border-bottom pb-1">{destination}</h6>
                  {routes.map((route) => (
                    <Form.Check
                      key={route.id}
                      type="checkbox"
                      id={`route-${route.id}`}
                      label={(
                        <small>
                          {route.origin.name}
                          {' '}
                          <i className="fa fa-arrow-right mx-1" />
                          {' '}
                          {route.destination.name}
                        </small>
                      )}
                      checked={selectedRoutes.includes(route.id)}
                      onChange={() => handleRouteToggle(route.id)}
                      className="mb-1"
                    />
                  ))}
                </div>
              ))}
              {availableRoutes.length === 0 && (
                <p className="text-muted text-center">Loading routes...</p>
              )}
            </Card.Body>
            <Card.Footer className="text-muted">
              <small>
                {selectedRoutes.length}
                {' '}
                of
                {' '}
                {availableRoutes.length}
                {' '}
                routes selected
              </small>
            </Card.Footer>
          </Card>
        </Col>

        {/* Category Selection */}
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Vehicle Categories</strong>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleSelectAllCategories}
              >
                {selectedCategories.length === vehicleCategories.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Card.Header>
            <Card.Body>
              {vehicleCategories.map((category) => (
                <Form.Check
                  key={category.Id}
                  type="checkbox"
                  id={`category-${category.Id}`}
                  label={(
                    <>
                      {category.name}
                      {' '}
                      <Badge bg="secondary" className="ms-2">
                        {category.capacity}
                        {' '}
                        pax
                      </Badge>
                    </>
                  )}
                  checked={selectedCategories.includes(category.Id)}
                  onChange={() => handleCategoryToggle(category.Id)}
                  className="mb-2"
                />
              ))}
              {vehicleCategories.length === 0 && (
                <p className="text-muted text-center">No vehicle categories available</p>
              )}
            </Card.Body>
            <Card.Footer className="text-muted">
              <small>
                {selectedCategories.length}
                {' '}
                of
                {' '}
                {vehicleCategories.length}
                {' '}
                categories selected
              </small>
            </Card.Footer>
          </Card>

          {/* Currency Selection */}
          <Card className="mb-3">
            <Card.Header>
              <strong>Currencies</strong>
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="checkbox"
                id="currency-eur"
                label={(
                  <>
                    <i className="fa fa-euro-sign me-2" />
                    EUR
                  </>
                )}
                checked={selectedCurrencies.includes('EUR')}
                onChange={() => handleCurrencyToggle('EUR')}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                id="currency-huf"
                label={(
                  <>
                    <i className="fa fa-coins me-2" />
                    HUF
                  </>
                )}
                checked={selectedCurrencies.includes('HUF')}
                onChange={() => handleCurrencyToggle('HUF')}
              />
              <small className="text-muted d-block mt-2">
                At least one currency must be selected
              </small>
            </Card.Body>
          </Card>

          {/* Format Selection */}
          <Card className="mb-3">
            <Card.Header>
              <strong>Export Format</strong>
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="radio"
                id="format-excel"
                name="format"
                label={(
                  <>
                    <i className="fa fa-file-excel me-2 text-success" />
                    Excel (.xlsx)
                  </>
                )}
                checked={format === 'excel'}
                onChange={() => setFormat('excel')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="format-csv"
                name="format"
                label={(
                  <>
                    <i className="fa fa-file-csv me-2 text-primary" />
                    CSV (.csv)
                  </>
                )}
                checked={format === 'csv'}
                onChange={() => setFormat('csv')}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div className="d-flex gap-2 mb-4">
        <Button
          variant="outline-primary"
          onClick={handlePreview}
          disabled={loading}
        >
          {loading && <Spinner size="sm" className="me-2" />}
          <i className="fa fa-eye me-2" />
          Preview
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={loading}
        >
          {loading && <Spinner size="sm" className="me-2" />}
          <i className="fa fa-download me-2" />
          Download
          {' '}
          {format === 'excel' ? 'Excel' : 'CSV'}
        </Button>
      </div>

      {/* Download Info Alert for async Excel generation */}
      {downloadInfo && (
        <Alert variant="info" onClose={() => setDownloadInfo(null)} dismissible className="mb-4">
          <Alert.Heading>
            <i className="fa fa-clock me-2" />
            Excel Report is Being Generated
          </Alert.Heading>
          <p>{downloadInfo.message}</p>
          <hr />
          <div className="d-flex align-items-center">
            <Button
              variant="outline-info"
              href={downloadInfo.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-download me-2" />
              Download:
              {' '}
              {downloadInfo.filename}
            </Button>
            <small className="text-muted ms-3">
              Link valid for 12 hours
            </small>
          </div>
        </Alert>
      )}

      {/* Preview Table */}
      {previewData && (
        <Card>
          <Card.Header>
            <strong>Preview</strong>
            <Badge bg="info" className="ms-2">
              {previewData.length}
              {' '}
              rows
            </Badge>
          </Card.Header>
          <Card.Body style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead className="sticky-top bg-white">
                <tr>
                  <th>Route</th>
                  <th>Origin</th>
                  <th>Destination</th>
                  <th>Vehicle</th>
                  {selectedCurrencies.includes('EUR') && <th>EUR</th>}
                  {selectedCurrencies.includes('HUF') && <th>HUF</th>}
                  <th>Distance</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {previewData.slice(0, 50).map((row) => (
                  <tr key={`${row.routeName}-${row.vehicleCategory}`}>
                    <td><small>{row.routeName}</small></td>
                    <td><small>{row.origin}</small></td>
                    <td><small>{row.destination}</small></td>
                    <td><small>{row.vehicleCategory}</small></td>
                    {selectedCurrencies.includes('EUR') && (
                      <td>
                        <small>
                          {typeof row.priceEUR === 'number' ? `€${row.priceEUR}` : row.priceEUR}
                        </small>
                      </td>
                    )}
                    {selectedCurrencies.includes('HUF') && (
                      <td>
                        <small>
                          {typeof row.priceHUF === 'number' ? `${row.priceHUF.toLocaleString()} Ft` : row.priceHUF}
                        </small>
                      </td>
                    )}
                    <td>
                      <small>
                        {typeof row.distanceKm === 'number' ? `${row.distanceKm} km` : row.distanceKm}
                      </small>
                    </td>
                    <td>
                      <Badge bg={row.priceType === 'predefined' ? 'success' : 'info'}>
                        {row.priceType}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {previewData.length > 50 && (
              <p className="text-muted text-center mt-2">
                Showing first 50 of
                {' '}
                {previewData.length}
                {' '}
                rows. Download for full report.
              </p>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default PricingReport;
